import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Container,
  FormControl,
  Grid,
  InputBase,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Button } from "@mui/material";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../../platform/components/auth/auth-context";
import { useSelectedBusiness } from "../../../platform/redux/store";
import {
  hasCareWorkspaceAccess,
  hasStandaloneBookingAccess,
} from "../../../core/business/productAccess";
import {
  BusinessCategoryLike,
  getDisplayedBusinessTopLevelCategories,
  isHealthcareCategoryName,
} from "../../../core/business/category";
import {
  BUSINESSES_PAGED,
  GET_ACTIVE_BUSINESS_CATEGORIES,
  GET_BUSINESS_CATEGORIES,
} from "../../../platform/graphql/queries";
import { BusinessData } from "../../../platform/types";
import BookingBusinessCardList from "../components/BookingBusinessCardList";
import SeoMeta from "../../../platform/components/common/seoMeta";
import {
  getAvailableBookingSubcategories,
  getBookingCategoryDisplayName,
  getResolvedBookingBusinessSubcategory,
  getResolvedBookingBusinessSubcategoryId,
  getResolvedBookingBusinessTopCategory,
  getResolvedBookingBusinessTopCategoryId,
  hasBookingCareSignals,
} from "../utils/bookingCategoryFilters";

function hasPublicBookingListing(business: BusinessData | any) {
  const bookingTypes =
    business?.businessBookingTypes ?? business?.businessBasic?.businessBookingTypes ?? [];
  return (
    (business?.businessBasic?.displayOnBookingPage === true ||
      business?.displayOnBookingPage === true) &&
    bookingTypes.some((bookingType: any) => bookingType?.isPublic === true)
  );
}

type BookingListingFilter = "all" | "event" | "service" | "class";

const BOOKING_LISTING_FILTERS: { value: BookingListingFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "event", label: "Events" },
  { value: "service", label: "Services" },
  { value: "class", label: "Classes" },
];

const BOOKING_TYPE_CATEGORY_BY_FILTER: Record<Exclude<BookingListingFilter, "all">, number> = {
  event: 1,
  service: 2,
  class: 3,
};

function matchesBookingListingFilter(
  business: BookingMarketplaceBusiness | any,
  filter: BookingListingFilter
) {
  if (filter === "all") return hasPublicBookingListing(business);

  const bookingTypes =
    business?.businessBookingTypes ?? business?.businessBasic?.businessBookingTypes ?? [];
  const targetCategoryId = BOOKING_TYPE_CATEGORY_BY_FILTER[filter];

  return (Array.isArray(bookingTypes) ? bookingTypes : []).some(
    (bookingType: any) =>
      bookingType?.isPublic === true &&
      Number(bookingType?.bookingTypeCategoryId || 0) === Number(targetCategoryId)
  );
}

function matchesBusinessCategoryFilter(
  business: BookingMarketplaceBusiness | any,
  categoryById: Map<number, BusinessCategoryLike>,
  healthcareCategory: BusinessCategoryLike | null,
  selectedCategoryId: number | null,
  selectedSubcategoryId: number | null
) {
  if (selectedSubcategoryId) {
    return (
      getResolvedBookingBusinessSubcategoryId(business, categoryById) ===
      selectedSubcategoryId
    );
  }
  if (selectedCategoryId) {
    return (
      getResolvedBookingBusinessTopCategoryId(
        business,
        categoryById,
        healthcareCategory
      ) ===
      selectedCategoryId
    );
  }
  return true;
}

function normalizeBrowseCategory(category: any): BrowseCategoryItem | null {
  const rawName = String(category?.name || "").trim();
  if (!rawName) return null;

  const normalizedName = isHealthcareCategoryName(rawName) ? "Healthcare" : rawName;
  return {
    id: category?.id,
    name: normalizedName,
  };
}

type BrowseCategoryItem = {
  id?: string | number | null;
  name: string;
};

type ActiveCityCategoryItem = {
  city?: string | null;
  cityId?: string | number | null;
  categories?: BusinessCategoryLike[];
};

type BookingMarketplaceBusiness = BusinessData & {
  businessCategoryId?: number | null;
  businessSubcategoryId?: number | null;
  cityId?: number | null;
  cityName?: string | null;
  businessTopCategory?: BusinessCategoryLike | null;
  businessSubcategory?: BusinessCategoryLike | null;
};

function isBrowseCategoryItem(value: BrowseCategoryItem | null): value is BrowseCategoryItem {
  return Boolean(value?.name);
}

function getBusinessCityName(business: BookingMarketplaceBusiness | any) {
  const explicitCity = String(business?.cityName || "").trim();
  if (explicitCity) return explicitCity;

  const address = String(business?.address || "").trim();
  if (!address) return "";

  const addressParts = address
    .split(",")
    .map((part) => String(part || "").trim())
    .filter(Boolean);

  return addressParts.length >= 2 ? addressParts[1] : "";
}

function getMarketplaceBrowseBusinessCategory(
  business: BookingMarketplaceBusiness | any,
  categoryById: Map<number, BusinessCategoryLike>,
  healthcareCategory: BusinessCategoryLike | null
) {
  const topCategoryId = getResolvedBookingBusinessTopCategoryId(
    business,
    categoryById,
    healthcareCategory
  );
  const topCategory =
    categoryById.get(topCategoryId) ||
    getResolvedBookingBusinessTopCategory(business, categoryById, healthcareCategory) ||
    null;

  if (topCategory && Number(topCategory?.id || 0) > 0) {
    return {
      id: Number(topCategory.id),
      name: getBookingCategoryDisplayName(topCategory),
    };
  }

  if (
    hasBookingCareSignals(business) &&
    healthcareCategory &&
    Number(healthcareCategory?.id || 0) > 0 &&
    topCategory == null
  ) {
    return {
      id: Number(healthcareCategory.id),
      name: getBookingCategoryDisplayName(healthcareCategory),
    };
  }

  return null;
}

export default function BookingMarketplacePage(): JSX.Element {
  const theme = useTheme();
  const navigate = useNavigate();
  const { providerUser } = React.useContext(UserContext);
  const selectedBusiness = useSelectedBusiness();
  const [searchQuery, setSearchQuery] = React.useState("");
  const [expandedAccordion, setExpandedAccordion] = React.useState<string | false>(false);
  const [visibleCount, setVisibleCount] = React.useState(5);
  const [listingFilter, setListingFilter] = React.useState<BookingListingFilter>("all");
  const [selectedCategoryId, setSelectedCategoryId] = React.useState<number | null>(null);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const html = document.documentElement;
    const body = document.body;
    const previousHtmlScrollbarWidth = html.style.getPropertyValue("scrollbar-width");
    const previousBodyScrollbarWidth = body.style.getPropertyValue("scrollbar-width");
    const previousMsOverflowStyle = body.style.getPropertyValue("-ms-overflow-style");

    html.style.setProperty("scrollbar-width", "none");
    body.style.setProperty("scrollbar-width", "none");
    body.style.setProperty("-ms-overflow-style", "none");

    const styleTag = document.createElement("style");
    styleTag.setAttribute("data-booking-public-scrollbar", "marketplace");
    styleTag.textContent = `
      html::-webkit-scrollbar,
      body::-webkit-scrollbar {
        width: 0 !important;
        height: 0 !important;
        display: none !important;
        background: transparent !important;
      }
    `;
    document.head.appendChild(styleTag);

    return () => {
      if (previousHtmlScrollbarWidth) {
        html.style.setProperty("scrollbar-width", previousHtmlScrollbarWidth);
      } else {
        html.style.removeProperty("scrollbar-width");
      }
      if (previousBodyScrollbarWidth) {
        body.style.setProperty("scrollbar-width", previousBodyScrollbarWidth);
      } else {
        body.style.removeProperty("scrollbar-width");
      }
      if (previousMsOverflowStyle) {
        body.style.setProperty("-ms-overflow-style", previousMsOverflowStyle);
      } else {
        body.style.removeProperty("-ms-overflow-style");
      }
      styleTag.remove();
    };
  }, []);

  const currentCountryId = Number(localStorage.getItem("currentCountryId") || "1");
  const featuredQueryLimit = Math.max(20, visibleCount * 3);

  const { data: categoriesData } = useQuery(GET_BUSINESS_CATEGORIES);
  const { data: featuredBusinessesData, loading: businessesLoading } = useQuery(BUSINESSES_PAGED, {
    variables: {
      countryId: currentCountryId,
      limit: featuredQueryLimit,
      offset: 0,
      search: searchQuery.trim() || undefined,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
  });
  const { data: activeCityCategoriesData } = useQuery(GET_ACTIVE_BUSINESS_CATEGORIES, {
    variables: {
      countryId: currentCountryId,
    },
    fetchPolicy: "cache-first",
  });

  const categoryById = React.useMemo(
    () =>
      new Map<number, BusinessCategoryLike>(
        (categoriesData?.getBusinessCategories || []).map((category: any) => [
          Number(category?.id || 0),
          category as BusinessCategoryLike,
        ])
      ),
    [categoriesData]
  );

  const healthcareTopCategory = React.useMemo(
    () =>
      (
        getDisplayedBusinessTopLevelCategories(
          (categoriesData?.getBusinessCategories || []) as BusinessCategoryLike[]
        ) || []
      ).find((categoryItem) => isHealthcareCategoryName(String(categoryItem?.name || "").trim())) || null,
    [categoriesData]
  );

  const marketplaceSourceBusinesses = React.useMemo(
    () =>
      (featuredBusinessesData?.businessesPaged?.items || []).filter((business: BookingMarketplaceBusiness) =>
        matchesBookingListingFilter(business, listingFilter)
      ),
    [featuredBusinessesData, listingFilter]
  );

  const marketplaceTopCategories = React.useMemo(() => {
    const topLevelById = new Map<number, BusinessCategoryLike>();
    const availableTopCategoryIds = new Set<number>();

    marketplaceSourceBusinesses.forEach((business: BookingMarketplaceBusiness) => {
      const topCategoryId = getResolvedBookingBusinessTopCategoryId(
        business,
        categoryById,
        healthcareTopCategory
      );
      if (!topCategoryId) return;
      availableTopCategoryIds.add(topCategoryId);
    });

    getDisplayedBusinessTopLevelCategories(
      (categoriesData?.getBusinessCategories || []) as BusinessCategoryLike[]
    ).forEach((category) => {
      const categoryId = Number(category?.id || 0);
      if (availableTopCategoryIds.has(categoryId)) {
        topLevelById.set(categoryId, category);
      }
    });

    marketplaceSourceBusinesses.forEach((business: BookingMarketplaceBusiness) => {
      const topCategoryId = getResolvedBookingBusinessTopCategoryId(
        business,
        categoryById,
        healthcareTopCategory
      );
      if (!topCategoryId || topLevelById.has(topCategoryId)) return;
      const fallbackCategory =
        categoryById.get(topCategoryId) ||
        getResolvedBookingBusinessTopCategory(
          business,
          categoryById,
          healthcareTopCategory
        ) ||
        null;
      if (fallbackCategory) {
        topLevelById.set(topCategoryId, fallbackCategory);
      }
    });

    return Array.from(topLevelById.values()).sort((left, right) =>
      getBookingCategoryDisplayName(left).localeCompare(
        getBookingCategoryDisplayName(right)
      )
    );
  }, [categoriesData, categoryById, healthcareTopCategory, marketplaceSourceBusinesses]);

  const selectedCategorySubcategories = React.useMemo(() => {
    return getAvailableBookingSubcategories({
      selectedCategoryId,
      categories: (categoriesData?.getBusinessCategories || []) as BusinessCategoryLike[],
      categoryById,
      healthcareCategory: healthcareTopCategory,
      businesses: marketplaceSourceBusinesses,
    });
  }, [
    categoriesData,
    categoryById,
    healthcareTopCategory,
    marketplaceSourceBusinesses,
    selectedCategoryId,
  ]);

  const selectedCategoryLabel = React.useMemo(() => {
    if (!selectedCategoryId) return "";
    const category =
      marketplaceTopCategories.find(
        (item) => Number(item?.id || 0) === Number(selectedCategoryId)
      ) || categoryById.get(Number(selectedCategoryId));
    return getBookingCategoryDisplayName(category);
  }, [categoryById, marketplaceTopCategories, selectedCategoryId]);
  const selectedSubcategoryLabel = React.useMemo(() => {
    if (!selectedSubcategoryId) return "";
    const subcategory =
      selectedCategorySubcategories.find(
        (item) => Number(item?.id || 0) === Number(selectedSubcategoryId)
      ) ||
      getResolvedBookingBusinessSubcategory(
        { businessSubcategoryId: selectedSubcategoryId },
        categoryById
      ) ||
      categoryById.get(Number(selectedSubcategoryId));
    return getBookingCategoryDisplayName(subcategory);
  }, [categoryById, selectedCategorySubcategories, selectedSubcategoryId]);

  const featuredBusinesses = React.useMemo(() => {
    const filtered = marketplaceSourceBusinesses.filter((business: BookingMarketplaceBusiness) =>
      matchesBusinessCategoryFilter(
        business,
        categoryById,
        healthcareTopCategory,
        selectedCategoryId,
        selectedSubcategoryId
      )
    );
    return filtered.slice(0, visibleCount);
  }, [
    categoryById,
    healthcareTopCategory,
    marketplaceSourceBusinesses,
    selectedCategoryId,
    selectedSubcategoryId,
    visibleCount,
  ]);
  const browseCities = React.useMemo(() => {
    const fallbackCityMap = new Map<
      string,
      { city: string; cityId: number | null; categories: Map<number, BrowseCategoryItem> }
    >();

    marketplaceSourceBusinesses.forEach((business: BookingMarketplaceBusiness) => {
      const explicitCityId = Number(business?.cityId || 0);
      const cityId = explicitCityId > 0 ? explicitCityId : null;
      const cityName = getBusinessCityName(business);
      if (!cityName) return;

      const cityKey = cityId ? String(cityId) : cityName.toLowerCase();

      if (!fallbackCityMap.has(cityKey)) {
        fallbackCityMap.set(cityKey, {
          city: cityName,
          cityId,
          categories: new Map<number, BrowseCategoryItem>(),
        });
      }

      const cityEntry = fallbackCityMap.get(cityKey);
      if (!cityEntry) return;

      const browseCategory = getMarketplaceBrowseBusinessCategory(
        business,
        categoryById,
        healthcareTopCategory
      );
      const categoryId = Number(browseCategory?.id || 0);
      if (!categoryId || !browseCategory) return;

      cityEntry.categories.set(categoryId, browseCategory);
    });

    const mergedCityMap = new Map<
      string,
      { city: string; cityId: number | null; categories: BrowseCategoryItem[] }
    >(
      Array.from(fallbackCityMap.entries()).map(([key, cityEntry]) => [
        key,
        {
          city: cityEntry.city,
          cityId: cityEntry.cityId,
          categories: Array.from(cityEntry.categories.values()).sort((a, b) =>
            String(a.name).localeCompare(String(b.name))
          ),
        },
      ])
    );

    ((activeCityCategoriesData?.getBookingTypesByActiveCities || []) as ActiveCityCategoryItem[]).forEach(
      (cityData: ActiveCityCategoryItem) => {
        const city = String(cityData?.city || "").trim();
        const cityId = Number(cityData?.cityId || 0) || null;
        if (!city) return;

        const categories = (cityData?.categories || [])
          .map((category: any) => normalizeBrowseCategory(category))
          .filter(isBrowseCategoryItem)
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));

        if (!categories.length) return;

        const cityKey = cityId ? String(cityId) : city.toLowerCase();
        mergedCityMap.set(cityKey, {
          city,
          cityId,
          categories,
        });
      }
    );

    return Array.from(mergedCityMap.values())
      .map((cityEntry) => ({
        city: cityEntry.city,
        cityId: cityEntry.cityId,
        categories: cityEntry.categories,
      }))
      .filter((cityData) => Boolean(cityData.city) && cityData.categories.length > 0)
      .sort((a, b) => String(a.city).localeCompare(String(b.city)));
  }, [activeCityCategoriesData, categoryById, healthcareTopCategory, marketplaceSourceBusinesses]);
  const filteredFeaturedCount = React.useMemo(
    () =>
      marketplaceSourceBusinesses.filter((business: BookingMarketplaceBusiness) =>
        matchesBusinessCategoryFilter(
          business,
          categoryById,
          healthcareTopCategory,
          selectedCategoryId,
          selectedSubcategoryId
        )
      ).length,
    [
      categoryById,
      healthcareTopCategory,
      marketplaceSourceBusinesses,
      selectedCategoryId,
      selectedSubcategoryId,
    ]
  );
  const compactFilterControlSx = React.useMemo(
    () => ({
      minWidth: { xs: "100%", sm: 220 },
      "& .MuiOutlinedInput-root": {
        minHeight: 40,
        borderRadius: 999,
        backgroundColor: alpha(theme.palette.primary.main, 0.035),
        fontWeight: 600,
        "& fieldset": {
          borderColor: alpha(theme.palette.primary.main, 0.16),
        },
        "&:hover fieldset": {
          borderColor: alpha(theme.palette.primary.main, 0.26),
        },
        "&.Mui-focused fieldset": {
          borderColor: theme.palette.primary.main,
          borderWidth: 1,
        },
      },
      "& .MuiSelect-select": {
        py: 1,
      },
    }),
    [theme]
  );
  const hasWorkspace = Boolean(providerUser?.userId && (selectedBusiness?.businessBasic?.id || providerUser?.defaultBusinessId));
  const canUseStandaloneBooking = hasStandaloneBookingAccess(providerUser);
  const canUseCareWorkspace = hasCareWorkspaceAccess(providerUser);

  React.useEffect(() => {
    setVisibleCount(5);
  }, [searchQuery, listingFilter, selectedCategoryId, selectedSubcategoryId]);

  React.useEffect(() => {
    if (
      selectedCategoryId &&
      !marketplaceTopCategories.some(
        (category) => Number(category?.id || 0) === Number(selectedCategoryId)
      )
    ) {
      setSelectedCategoryId(null);
      setSelectedSubcategoryId(null);
    }
  }, [marketplaceTopCategories, selectedCategoryId]);

  React.useEffect(() => {
    if (
      selectedSubcategoryId &&
      !selectedCategorySubcategories.some(
        (subcategory) => Number(subcategory?.id || 0) === Number(selectedSubcategoryId)
      )
    ) {
      setSelectedSubcategoryId(null);
    }
  }, [selectedCategorySubcategories, selectedSubcategoryId]);

  const handleCategorySelect = React.useCallback((nextCategoryId: number | null) => {
    setSelectedCategoryId(nextCategoryId);
    setSelectedSubcategoryId(null);
  }, []);

  const handleSubcategorySelect = React.useCallback((nextSubcategoryId: number | null) => {
    setSelectedSubcategoryId(nextSubcategoryId);
  }, []);

  const handleRegisterBookingBusiness = React.useCallback(() => {
    navigate("/biz/business?v=booking&new=1", {
      state: {
        forceNewBusiness: true,
        verticalId: "booking",
      },
    });
  }, [navigate]);

  const handleOpenWorkspace = React.useCallback(() => {
    if (canUseStandaloneBooking) {
      navigate("/biz/bookingtypes?v=booking");
      return;
    }
    if (canUseCareWorkspace) {
      navigate("/biz/dashboard?v=care");
      return;
    }
    navigate("/biz/business?v=booking");
  }, [canUseCareWorkspace, canUseStandaloneBooking, navigate]);

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    navigate(`/search?v=booking&q=${encodeURIComponent(searchQuery)}`);
  };

  const handleAccordionChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedAccordion(isExpanded ? panel : false);
    };
  const seoUrl =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : "/booking";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        color: "text.primary",
      }}
    >
      <SeoMeta
        title="Find Booking Pages, Services, and Events | Timely365"
        description="Browse public booking pages, services, classes, and events on Timely365."
        url={seoUrl}
      />
      <Box
        sx={{
          background: "linear-gradient(135deg, #22374a 0%, #314f67 100%)",
          color: theme.palette.primary.contrastText,
          textAlign: "center",
          px: 2,
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight={800}>
            Discover Public Booking Pages
          </Typography>
          <Typography variant="subtitle1" sx={{ mt: 1.5, opacity: 0.9 }}>
            Explore businesses, services, events, and classes that are open for online booking.
          </Typography>
          <Box
            sx={{
              mt: 3,
              display: "flex",
              gap: 1.5,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            {hasWorkspace ? (
              <Button variant="contained" color="secondary" onClick={handleOpenWorkspace}>
                Manage Business
              </Button>
            ) : providerUser?.userId ? (
              <Button variant="contained" color="secondary" onClick={handleRegisterBookingBusiness}>
                Register your booking business
              </Button>
            ) : (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => navigate("/booking/start")}
                >
                  Get started with Booking
                </Button>
                <Button
                  variant="outlined"
                  sx={{ color: "white", borderColor: "rgba(255,255,255,0.5)" }}
                  onClick={() => navigate("/login?v=booking")}
                >
                  Sign in
                </Button>
              </>
            )}
          </Box>

          <Paper
            component="form"
            onSubmit={handleSearchSubmit}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              maxWidth: 680,
              mx: "auto",
              mt: 4,
              px: 2,
              py: 1,
              borderRadius: 3,
            }}
          >
            <SearchIcon color="action" />
            <InputBase
              placeholder="Search services, events, classes, or businesses"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ color: "text.primary" }}
            />
          </Paper>
        </Container>
      </Box>

      <Container sx={{ py: 4 }}>
        <Typography variant="h5" fontWeight={800} gutterBottom>
          Featured Booking Businesses
        </Typography>
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: { xs: 1, md: 1.25 },
            borderRadius: 2.5,
            border: "1px solid",
            borderColor: alpha(theme.palette.primary.main, 0.1),
            backgroundColor: alpha(theme.palette.background.paper, 0.96),
          }}
        >
          <Stack spacing={1}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", lg: "center" }}
            >
              {marketplaceTopCategories.length > 0 ? (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <FormControl size="small" sx={compactFilterControlSx}>
                    <Select
                      displayEmpty
                      value={selectedCategoryId ?? ""}
                      onChange={(event) => {
                        const nextValue = Number(event.target.value || 0);
                        handleCategorySelect(nextValue > 0 ? nextValue : null);
                      }}
                      renderValue={(value) =>
                        value ? selectedCategoryLabel || "Category" : "Category"
                      }
                    >
                      <MenuItem value="">All categories</MenuItem>
                      {marketplaceTopCategories.map((category) => (
                        <MenuItem key={Number(category?.id || 0)} value={Number(category?.id || 0)}>
                          {getBookingCategoryDisplayName(category)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedCategoryId && selectedCategorySubcategories.length > 0 ? (
                    <FormControl size="small" sx={compactFilterControlSx}>
                      <Select
                        displayEmpty
                        value={selectedSubcategoryId ?? ""}
                        onChange={(event) => {
                          const nextValue = Number(event.target.value || 0);
                          handleSubcategorySelect(nextValue > 0 ? nextValue : null);
                        }}
                        renderValue={(value) =>
                          value ? selectedSubcategoryLabel || "Subcategory" : "Subcategory"
                        }
                      >
                        <MenuItem value="">All in {selectedCategoryLabel || "category"}</MenuItem>
                        {selectedCategorySubcategories.map((subcategory) => (
                          <MenuItem
                            key={Number(subcategory?.id || 0)}
                            value={Number(subcategory?.id || 0)}
                          >
                            {getBookingCategoryDisplayName(subcategory)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : null}
                </Stack>
              ) : (
                <Box sx={{ flex: 1 }} />
              )}

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent={{ xs: "flex-start", lg: "flex-end" }}
                useFlexGap
                flexWrap="wrap"
              >
                <Chip
                  size="small"
                  label={`${filteredFeaturedCount} match${filteredFeaturedCount === 1 ? "" : "es"}`}
                  sx={{
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: "primary.main",
                  }}
                />
                {BOOKING_LISTING_FILTERS.map((filterOption) => {
                  const selected = listingFilter === filterOption.value;
                  return (
                    <Button
                      key={filterOption.value}
                      size="small"
                      variant={selected ? "contained" : "outlined"}
                      color={selected ? "primary" : "inherit"}
                      onClick={() => setListingFilter(filterOption.value)}
                      sx={{
                        minWidth: 78,
                        borderRadius: 999,
                        textTransform: "none",
                        px: 1.1,
                      }}
                    >
                      {filterOption.label}
                    </Button>
                  );
                })}
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        {businessesLoading ? (
          <Typography color="text.secondary">Loading booking businesses...</Typography>
        ) : null}
        <BookingBusinessCardList businesses={featuredBusinesses} />
        {filteredFeaturedCount > visibleCount && (
          <Box
            sx={{
              mt: 2.5,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setVisibleCount((current) => current + 10)}
            >
              More
            </Button>
          </Box>
        )}
      </Container>

      <Container maxWidth="lg" sx={{ pb: 6 }}>
        <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
          Browse by city
        </Typography>
        <Grid container spacing={2}>
          {browseCities.map(
            (cityData: any, index: number) => {
              if (!Array.isArray(cityData?.categories) || cityData.categories.length === 0) {
                return null;
              }
              const panelId = `booking-city-${index}`;
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={panelId}>
                  <Accordion
                    expanded={expandedAccordion === panelId}
                    onChange={handleAccordionChange(panelId)}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography fontWeight={700}>{cityData.city}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {cityData.categories.map((category: any) => (
                          <Typography
                            key={`${panelId}-${category.id ?? "all-listings"}`}
                            sx={{
                              cursor: "pointer",
                              color: "primary.main",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={() => {
                              if (category?.id == null || !cityData?.cityId) {
                                navigate(`/search?v=booking&city=${encodeURIComponent(cityData.city)}`);
                                return;
                              }
                              navigate(
                                `/booking/category/${category.id}_${String(category.name)
                                  .replace(/\s+/g, "_")
                                  .toLowerCase()}?city=${cityData.cityId}_${cityData.city}`
                              );
                            }}
                          >
                            {category.name}
                          </Typography>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              );
            }
          )}
        </Grid>
      </Container>
    </Box>
  );
}
